import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Switch,
} from "@mui/material";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PauseCircleRoundedIcon from "@mui/icons-material/PauseCircleRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PlayCircleRoundedIcon from "@mui/icons-material/PlayCircleRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useParams, useSearchParams } from "react-router-dom";
import { planBillingApi } from "../../api/planBilling";
import MercadoPago3DSChallenge from "../../components/payments/MercadoPago3DSChallenge";
import MercadoPagoCardForm from "../../components/payments/MercadoPagoCardForm";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import { useProjects } from "../../context/ProjectsContext";
import { getProjectBillingAccess } from "../../utils/projectBillingAccess";

const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#D94B2D",
  dark: "#19242F",
  muted: "rgba(25,36,47,.64)",
  subtle: "rgba(25,36,47,.045)",
  border: "rgba(25,36,47,.10)",
};

const SERVICE_PRESENTATION = {
  landing: {
    icon: LanguageRoundedIcon,
    eyebrow: "Presencia online",
    benefit: "Mostrá tu negocio, recibí consultas y mantené una web profesional siempre visible.",
  },
  products: {
    icon: Inventory2RoundedIcon,
    eyebrow: "Catálogo",
    benefit: "Publicá productos o servicios con imágenes, precios, categorías y datos adicionales.",
  },
  orders: {
    icon: ShoppingCartRoundedIcon,
    eyebrow: "Ventas",
    benefit: "Recibí pedidos y seguí cada operación desde un flujo simple para tu equipo.",
  },
  payments: {
    icon: PaymentsRoundedIcon,
    eyebrow: "Cobros online",
    benefit: "Aceptá pagos con el Mercado Pago propio de tu comercio, sin mezclar cuentas.",
  },
  system: {
    icon: BusinessCenterRoundedIcon,
    eyebrow: "Gestión",
    benefit: "Ordená movimientos, tareas y procesos internos desde un solo lugar.",
  },
  stats: {
    icon: AnalyticsRoundedIcon,
    eyebrow: "Indicadores",
    benefit: "Entendé la actividad del negocio con métricas fáciles de leer y usar.",
  },
  webpush: {
    icon: NotificationsActiveRoundedIcon,
    eyebrow: "Notificaciones",
    benefit: "Recibí alertas y enviá novedades a las personas suscriptas a tu web.",
  },
};

function money(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "ARS" ? 0 : 2,
  }).format(Number(value || 0));
}

function dateLabel(value) {
  if (!value) return "A confirmar";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "A confirmar";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(date);
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function isValidEmail(value) {
  const email = normalizeEmail(value);
  return email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function statusLabel(status) {
  const labels = {
    trial: "Prueba gratis activa",
    pending: "Falta terminar el pago",
    active: "Activo",
    past_due: "Pago pendiente",
    paused: "Pausado",
    cancelled: "Cancelado",
    exempt: "Bonificado",
    authorized: "Autorizado",
    manual_pending: "Pendiente",
    pending_payment: "Pago pendiente",
    approved: "Pagado",
    expired: "Vencido",
    rejected: "Rechazado",
  };
  return labels[status] || status || "Sin activar";
}

function statusColor(status) {
  if (["active", "authorized", "approved", "exempt", "trial"].includes(status)) return "success";
  if (["pending", "pending_payment", "manual_pending", "past_due"].includes(status)) return "warning";
  if (["paused", "cancelled", "expired", "rejected"].includes(status)) return "error";
  return "default";
}

function uniqueCodes(values = []) {
  return [...new Set((Array.isArray(values) ? values : []).map(String).filter(Boolean))].sort();
}

function sameCodes(left = [], right = []) {
  const a = uniqueCodes(left);
  const b = uniqueCodes(right);
  return a.length === b.length && a.every((code, index) => code === b[index]);
}

function expandSelection(codes, services) {
  const byCode = new Map((services || []).map((service) => [service.code, service]));
  const selected = new Set();

  const add = (code, trail = []) => {
    if (!code || selected.has(code) || trail.includes(code)) return;
    const service = byCode.get(code);
    if (!service) return;
    for (const dependency of service.requiredServiceCodes || []) {
      add(dependency, [...trail, code]);
    }
    selected.add(code);
  };

  (codes || []).forEach((code) => add(code));
  return (services || [])
    .filter((service) => selected.has(service.code))
    .map((service) => service.code);
}

function removeServiceAndDependants(code, selectedCodes, services) {
  const selected = new Set(selectedCodes || []);
  selected.delete(code);

  let changed = true;
  while (changed) {
    changed = false;
    for (const service of services || []) {
      if (!selected.has(service.code)) continue;
      const missingDependency = (service.requiredServiceCodes || []).some(
        (dependency) => !selected.has(dependency),
      );
      if (missingDependency) {
        selected.delete(service.code);
        changed = true;
      }
    }
  }

  return (services || [])
    .filter((service) => selected.has(service.code))
    .map((service) => service.code);
}

function subscriptionServicesLabel(subscription) {
  const snapshotNames = (subscription?.serviceSnapshot || [])
    .map((service) => service?.name)
    .filter(Boolean);
  if (snapshotNames.length) return snapshotNames.join(" + ");
  const codes = subscription?.requestedServiceCodes || [];
  return codes.length ? codes.join(" + ") : subscription?.requestedPlanCode || "—";
}

function Step({ number, children }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Box
        sx={{
          width: 27,
          height: 27,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(255,255,255,.14)",
          fontSize: 13,
          fontWeight: 950,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,.82)", fontWeight: 800 }}>
        {children}
      </Typography>
    </Stack>
  );
}

function SummaryMetric({ icon, title, value, helper }) {
  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        p: 2.25,
        borderRadius: 3,
        border: `1px solid ${BRAND.border}`,
        bgcolor: "#fff",
      }}
    >
      <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.25,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(243,90,58,.10)",
            color: BRAND.orange,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: BRAND.muted,
              fontWeight: 900,
              letterSpacing: ".04em",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 0.15,
              color: BRAND.dark,
              fontSize: { xs: "1.08rem", md: "1.2rem" },
              lineHeight: 1.2,
              fontWeight: 950,
              overflowWrap: "anywhere",
            }}
          >
            {value}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.4,
              color: "rgba(25,36,47,.52)",
              lineHeight: 1.35,
            }}
          >
            {helper}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function ServiceRow({
  service,
  selected,
  activeNow,
  selectable,
  presentation,
  requiredNames,
  requiredBy,
  disabled,
  paymentNeedsAttention,
  onToggle,
}) {
  const ServiceIcon = presentation.icon || WorkspacePremiumRoundedIcon;
  const [expanded, setExpanded] = React.useState(false);
  const features = (service.features || []).filter(Boolean);
  const lockedByDependency = selected && requiredBy.length > 0;
  const controlDisabled = disabled || !selectable || lockedByDependency;

  const status = lockedByDependency
    ? "Necesario"
    : activeNow && selected
      ? "Incluido"
      : activeNow && !selected
        ? "Se quitará"
        : selected
          ? "Se agregará"
          : "Opcional";

  const statusSx = lockedByDependency
    ? { bgcolor: "rgba(2,136,209,.10)", color: "#075A79" }
    : activeNow && selected
      ? { bgcolor: "rgba(46,125,50,.11)", color: "#2E7D32" }
      : activeNow && !selected
        ? { bgcolor: "rgba(211,47,47,.09)", color: "#B3261E" }
        : selected
          ? { bgcolor: "rgba(243,90,58,.11)", color: BRAND.orangeDark }
          : { bgcolor: BRAND.subtle, color: BRAND.muted };

  return (
    <Box
      sx={{
        minWidth: 0,
        bgcolor: selected ? "rgba(243,90,58,.025)" : "#fff",
        borderLeft: selected ? `4px solid ${BRAND.orange}` : "4px solid transparent",
        transition: "background-color .18s ease, border-color .18s ease",
        "&:not(:last-of-type)": { borderBottom: `1px solid ${BRAND.border}` },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "44px minmax(0,1fr) auto",
            md: "44px minmax(220px,1fr) minmax(120px,.55fr) 132px 92px",
          },
          gap: { xs: 1.15, md: 1.75 },
          alignItems: "center",
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.5, sm: 1.75 },
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.25,
            display: "grid",
            placeItems: "center",
            bgcolor: selected ? "rgba(243,90,58,.11)" : BRAND.subtle,
            color: selected ? BRAND.orange : BRAND.dark,
            flexShrink: 0,
          }}
        >
          <ServiceIcon fontSize="small" />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ minWidth: 0 }}
          >
            <Typography
              sx={{
                color: BRAND.dark,
                fontWeight: 950,
                lineHeight: 1.2,
                overflowWrap: "anywhere",
              }}
            >
              {service.name}
            </Typography>
            <Chip
              size="small"
              label={status}
              sx={{ height: 23, fontSize: 10.5, fontWeight: 950, ...statusSx }}
            />
          </Stack>
          <Typography
            variant="body2"
            sx={{
              mt: 0.35,
              color: BRAND.muted,
              lineHeight: 1.4,
              display: "-webkit-box",
              WebkitLineClamp: expanded ? "unset" : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {presentation.benefit || service.description}
          </Typography>

          {lockedByDependency ? (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.65, color: "#075A79", fontWeight: 850 }}
            >
              Se mantiene activo porque lo necesita: {requiredBy.join(", ")}.
            </Typography>
          ) : requiredNames.length ? (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.65, color: BRAND.muted, fontWeight: 750 }}
            >
              Al activarlo también se incluyen: {requiredNames.join(", ")}.
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "block" },
            minWidth: 0,
          }}
        >
          <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 850 }}>
            {presentation.eyebrow || "Módulo WebProfit"}
          </Typography>
          <Button
            size="small"
            onClick={() => setExpanded((value) => !value)}
            endIcon={expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            sx={{
              display: "flex",
              mt: 0.15,
              minWidth: 0,
              px: 0,
              justifyContent: "flex-start",
              color: BRAND.dark,
              textTransform: "none",
              fontWeight: 850,
            }}
          >
            {expanded ? "Ocultar detalles" : "Ver qué incluye"}
          </Button>
        </Box>

        <Box sx={{ display: { xs: "none", md: "block" }, textAlign: "right" }}>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.05 }}>
            {money(service.monthlyPrice, service.currency)}
          </Typography>
          <Typography variant="caption" sx={{ color: BRAND.muted }}>
            por mes
          </Typography>
          {service.customPriceApplied ? (
            <Typography variant="caption" sx={{ display: "block", color: BRAND.orange, fontWeight: 800 }}>
              Precio especial
            </Typography>
          ) : null}
        </Box>

        <Stack alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
          <Switch
            checked={selected}
            disabled={controlDisabled}
            onChange={(event) => onToggle(event.target.checked)}
            inputProps={{ "aria-label": `${selected ? "Desactivar" : "Activar"} ${service.name}` }}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND.orange },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: BRAND.orange },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: { xs: "none", md: "block" },
              color: controlDisabled ? "rgba(25,36,47,.38)" : BRAND.muted,
              fontWeight: 800,
              textAlign: "center",
              lineHeight: 1.15,
            }}
          >
            {paymentNeedsAttention
              ? "Bloqueado"
              : lockedByDependency
                ? "Automático"
                : selected
                  ? "Activado"
                  : activeNow
                    ? "Se quitará"
                    : "Activar"}
          </Typography>
        </Stack>

        <Box
          sx={{
            gridColumn: { xs: "2 / -1", md: "auto" },
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mt: -0.35,
          }}
        >
          <Box>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.05 }}>
              {money(service.monthlyPrice, service.currency)}
            </Typography>
            <Typography variant="caption" sx={{ color: BRAND.muted }}>
              por mes
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setExpanded((value) => !value)}
            endIcon={expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            sx={{ color: BRAND.dark, textTransform: "none", fontWeight: 850 }}
          >
            {expanded ? "Cerrar" : "Detalles"}
          </Button>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mx: { xs: 1.5 },
            ml: { md: "60px" },
            mr: { md: 2 },
            mb: 1.6,
            p: 1.5,
            borderRadius: 2.25,
            bgcolor: BRAND.subtle,
          }}
        >
          <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950 }}>
            QUÉ INCLUYE
          </Typography>
          <Box
            sx={{
              mt: 1,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" },
              gap: 0.75,
            }}
          >
            {(features.length ? features : ["Herramientas principales del módulo"]).map((feature) => (
              <Stack key={feature} direction="row" spacing={0.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
                <CheckCircleRoundedIcon sx={{ mt: 0.1, fontSize: 16, color: "success.main", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: BRAND.dark, lineHeight: 1.35 }}>
                  {feature}
                </Typography>
              </Stack>
            ))}
          </Box>
          {!selectable ? (
            <Typography variant="caption" sx={{ display: "block", mt: 1, color: "error.main", fontWeight: 800 }}>
              Este módulo no está disponible para nuevas altas.
            </Typography>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}


function BillingPaymentChoice({
  paymentMode,
  amount,
  currency,
  publicKey,
  embeddedCardConfigured,
  payerEmail,
  onPayerEmailChange,
  payerEmailRequired,
  fallbackDisabled,
  fallbackLabel,
  onFallback,
  working,
  cardDisabled,
  onCardSubmit,
  cardError,
  threeDSChallenge,
  onThreeDSComplete,
  recurringActive,
}) {
  const [channel, setChannel] = React.useState(
    embeddedCardConfigured ? "card" : "mercadopago",
  );

  React.useEffect(() => {
    if (!embeddedCardConfigured && channel === "card") {
      setChannel("mercadopago");
    }
  }, [channel, embeddedCardConfigured]);

  return (
    <Box>
      <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950 }}>
        DÓNDE QUERÉS PAGAR
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={channel}
        onChange={(_event, value) => value && setChannel(value)}
        sx={{
          mt: 1,
          "& .MuiToggleButton-root": {
            minWidth: 0,
            py: 1,
            px: 1,
            gap: 0.65,
            textTransform: "none",
            fontSize: ".82rem",
            fontWeight: 900,
          },
        }}
      >
        <ToggleButton value="card" disabled={!embeddedCardConfigured}>
          <CreditCardRoundedIcon sx={{ fontSize: 18 }} />
          Tarjeta acá
        </ToggleButton>
        <ToggleButton value="mercadopago">
          <AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} />
          Mercado Pago
        </ToggleButton>
      </ToggleButtonGroup>

      <Collapse in={channel === "card"} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 2.5,
            border: `1px solid ${BRAND.border}`,
            bgcolor: "rgba(25,36,47,.018)",
          }}
        >
          {!embeddedCardConfigured ? (
            <Alert severity="warning">
              Falta configurar la Public Key de Mercado Pago para mostrar la tarjeta dentro del panel.
            </Alert>
          ) : threeDSChallenge ? (
            <MercadoPago3DSChallenge
              externalResourceUrl={threeDSChallenge.externalResourceUrl}
              creq={threeDSChallenge.creq}
              onComplete={onThreeDSComplete}
            />
          ) : (
            <MercadoPagoCardForm
              key={`${paymentMode}-${amount}-${publicKey}`}
              publicKey={publicKey}
              amount={amount}
              currency={currency}
              buyer={{ email: payerEmail }}
              processing={working}
              disabled={cardDisabled}
              compact
              hideHeader
              submitLabel={
                paymentMode === "recurring"
                  ? recurringActive
                    ? "Actualizar esta tarjeta"
                    : "Suscribirme con esta tarjeta"
                  : `Pagar ${money(amount, currency)}`
              }
              onSubmit={onCardSubmit}
              onError={() => {}}
            />
          )}
          {cardError ? (
            <Alert severity="error" sx={{ mt: 1.25 }}>
              {cardError}
            </Alert>
          ) : null}
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1.1, color: BRAND.muted, lineHeight: 1.45 }}
          >
            {paymentMode === "recurring"
              ? "La tarjeta queda asociada al débito mensual. El correo del formulario puede ser distinto al de WebProfit."
              : "Es un único cobro por el período. WebProfit no guarda los datos de la tarjeta."}
          </Typography>
        </Box>
      </Collapse>

      <Collapse in={channel === "mercadopago"} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 1.5,
            p: 1.6,
            borderRadius: 2.5,
            border: `1px solid ${BRAND.border}`,
            bgcolor: BRAND.subtle,
          }}
        >
          {paymentMode === "recurring" ? (
            <TextField
              fullWidth
              size="small"
              type="email"
              label="Correo de la cuenta de Mercado Pago"
              value={payerEmail}
              onChange={(event) => onPayerEmailChange(event.target.value)}
              error={payerEmailRequired && payerEmail.length > 0 && !isValidEmail(payerEmail)}
              helperText={
                payerEmailRequired && !payerEmail
                  ? "Ingresá el correo que autorizará el débito."
                  : "Puede ser distinto al correo de WebProfit."
              }
            />
          ) : null}
          <Typography
            variant="body2"
            sx={{ mt: paymentMode === "recurring" ? 1.25 : 0, color: BRAND.muted, lineHeight: 1.5 }}
          >
            {paymentMode === "recurring"
              ? "Te llevamos a Mercado Pago para autorizar la suscripción con tu cuenta o una tarjeta disponible."
              : "Te llevamos a Mercado Pago para pagar con saldo, tarjeta u otro medio habilitado."}
          </Typography>
          <Button
            fullWidth
            disabled={fallbackDisabled}
            onClick={onFallback}
            variant="contained"
            startIcon={
              working ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <AccountBalanceWalletRoundedIcon />
              )
            }
            sx={{
              mt: 1.5,
              minHeight: 46,
              bgcolor: "#009EE3",
              borderRadius: 2.25,
              textTransform: "none",
              fontWeight: 950,
              "&:hover": { bgcolor: "#008CC9" },
            }}
          >
            {fallbackLabel}
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}

function CheckoutSummary({
  selectedServices,
  selectedAmount,
  currency,
  billing,
  billingAccess,
  paymentNeedsAttention,
  paymentMode,
  onPaymentModeChange,
  payerEmail,
  onPayerEmailChange,
  payerEmailRequired,
  recurringLocked,
  oneTimeLocked,
  selectionChanged,
  actionLabel,
  primaryDisabled,
  primaryAction,
  working,
  mercadoPagoPublicKey,
  embeddedCardConfigured,
  cardDisabled,
  onCardSubmit,
  cardError,
  threeDSChallenge,
  onThreeDSComplete,
  recurringActive,
}) {
  const paymentSuspended = billingAccess?.reason === "billing_suspended";

  return (
    <Paper
      elevation={0}
      sx={{
        position: { lg: "sticky" },
        top: { lg: 20 },
        p: { xs: 2.25, sm: 2.75 },
        borderRadius: 3.5,
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 18px 46px rgba(25,36,47,.09)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          mx: { xs: -2.25, sm: -2.75 },
          mt: { xs: -2.25, sm: -2.75 },
          mb: 2.4,
          px: { xs: 2.25, sm: 2.75 },
          py: 2.1,
          bgcolor: BRAND.dark,
          color: "#fff",
        }}
      >
        <Typography variant="overline" sx={{ color: "rgba(255,255,255,.62)", fontWeight: 900 }}>
          RESUMEN DE TU PLAN
        </Typography>
        <Stack direction="row" spacing={0.7} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography
            sx={{
              fontSize: { xs: "2rem", sm: "2.25rem" },
              lineHeight: 1.05,
              fontWeight: 950,
              letterSpacing: "-.045em",
              overflowWrap: "anywhere",
            }}
          >
            {money(selectedAmount, currency)}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,.68)", fontWeight: 700 }}>
            {paymentMode === "one_time" ? "por este período" : "/ mes"}
          </Typography>
        </Stack>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>
            {selectedServices.length} {selectedServices.length === 1 ? "módulo" : "módulos"}
          </Typography>
          <Typography variant="caption" sx={{ color: BRAND.muted }}>
            {paymentMode === "one_time"
              ? "Un pago, sin renovación automática"
              : "Se renueva automáticamente cada mes"}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={billingAccess.isGrace
            ? "Pago pendiente"
            : paymentSuspended
              ? "Suspendido por pago"
              : billingAccess.isLandingOnly
                ? "Acceso limitado"
              : selectionChanged
                ? "Sin confirmar"
                : statusLabel(billing.status)}
          color={billingAccess.isLandingOnly || paymentSuspended
            ? "error"
            : billingAccess.isGrace || selectionChanged
              ? "warning"
              : statusColor(billing.status)}
          sx={{ maxWidth: 150, fontWeight: 900 }}
        />
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Box>
        <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950 }}>
          CÓMO QUERÉS PAGAR
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={paymentMode}
          onChange={(_event, value) => value && onPaymentModeChange(value)}
          sx={{ mt: 1, "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 900, py: 1 } }}
        >
          <ToggleButton value="recurring" disabled={recurringLocked}>
            Automático
          </ToggleButton>
          <ToggleButton value="one_time" disabled={oneTimeLocked}>
            Solo este período
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: BRAND.muted, lineHeight: 1.45 }}>
          {paymentMode === "one_time"
            ? "Mercado Pago cobra una sola vez. Al vencer el período tendrás días de gracia para volver a pagar."
            : "Mercado Pago autoriza una tarjeta compatible y vuelve a cobrar cada mes automáticamente."}
        </Typography>
        {recurringLocked ? (
          <Typography variant="caption" sx={{ display: "block", mt: 0.65, color: "warning.dark", fontWeight: 800 }}>
            El período actual ya está pagado. El débito automático podrá activarse cuando se acerque su vencimiento.
          </Typography>
        ) : null}
        {oneTimeLocked ? (
          <Typography variant="caption" sx={{ display: "block", mt: 0.65, color: "warning.dark", fontWeight: 800 }}>
            Ya existe una suscripción automática activa; no generamos otro cobro para evitar duplicados.
          </Typography>
        ) : null}

      </Box>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={1.05} sx={{ maxHeight: 210, overflowY: "auto", pr: 0.5 }}>
        {selectedServices.map((service) => (
          <Stack key={service.code} direction="row" justifyContent="space-between" spacing={1.25} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.8} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <CheckCircleRoundedIcon sx={{ mt: 0.15, fontSize: 17, color: "success.main", flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 750, overflowWrap: "anywhere" }}>
                {service.name}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 900, flexShrink: 0 }}>
              {money(service.monthlyPrice, service.currency)}
            </Typography>
          </Stack>
        ))}
        {!selectedServices.length ? (
          <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: BRAND.subtle }}>
            <Typography variant="body2" sx={{ color: BRAND.muted }}>
              Elegí al menos un módulo para armar el plan.
            </Typography>
          </Box>
        ) : null}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <BillingPaymentChoice
        paymentMode={paymentMode}
        amount={selectedAmount}
        currency={currency}
        publicKey={mercadoPagoPublicKey}
        embeddedCardConfigured={embeddedCardConfigured}
        payerEmail={payerEmail}
        onPayerEmailChange={onPayerEmailChange}
        payerEmailRequired={payerEmailRequired}
        fallbackDisabled={primaryDisabled}
        fallbackLabel={actionLabel}
        onFallback={primaryAction}
        working={working}
        cardDisabled={cardDisabled}
        onCardSubmit={onCardSubmit}
        cardError={cardError}
        threeDSChallenge={threeDSChallenge}
        onThreeDSComplete={onThreeDSComplete}
        recurringActive={recurringActive}
      />

      <Typography
        variant="caption"
        sx={{ display: "block", mt: 1.4, textAlign: "center", color: "rgba(25,36,47,.45)" }}
      >
        Los datos de tarjeta los procesa Mercado Pago. Este cobro no se mezcla con los cobros de tu tienda.
      </Typography>
    </Paper>
  );
}

export default function PlanBillingPage({ embedded = false }) {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loadProjects, setActiveProject } = useProjects();
  const [data, setData] = React.useState(null);
  const [selectedCodes, setSelectedCodes] = React.useState([]);
  const [paymentMode, setPaymentMode] = React.useState("recurring");
  const [payerEmail, setPayerEmail] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [working, setWorking] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [cardError, setCardError] = React.useState("");
  const [threeDSChallenge, setThreeDSChallenge] = React.useState(null);
  const returnHandled = React.useRef(false);
  const paymentModeInitialized = React.useRef(false);
  const payerEmailInitialized = React.useRef(false);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await planBillingApi.getProject(projectId);
      setData(response);
      if (response.project) setActiveProject(response.project);
    } catch (err) {
      setError(err.message || "No se pudo cargar tu plan");
    } finally {
      setLoading(false);
    }
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    paymentModeInitialized.current = false;
    payerEmailInitialized.current = false;
    returnHandled.current = false;
    load();
  }, [load]);

  React.useEffect(() => {
    const services = data?.services || [];
    const pending = data?.billing?.pendingServiceCodes || [];
    const current = data?.billing?.serviceCodes || [];
    setSelectedCodes(expandSelection(pending.length ? pending : current, services));
    if (!paymentModeInitialized.current && data?.billing) {
      setPaymentMode(
        data.billing.paymentMode === "one_time" ? "one_time" : "recurring",
      );
      paymentModeInitialized.current = true;
    }
    if (!payerEmailInitialized.current && data?.billing) {
      const lastSubscriptionEmail = (data.subscriptions || []).find(
        (subscription) => subscription?.payerEmail,
      )?.payerEmail;
      setPayerEmail(
        normalizeEmail(data.billing.payerEmail || lastSubscriptionEmail || ""),
      );
      payerEmailInitialized.current = true;
    }
  }, [data]);

  React.useEffect(() => {
    const paymentId =
      searchParams.get("payment_id") || searchParams.get("collection_id") || "";
    const returned =
      searchParams.get("subscription") ||
      searchParams.get("preapproval_id") ||
      searchParams.get("billing_payment") ||
      searchParams.get("status") ||
      paymentId;
    if (!returned || returnHandled.current) return;
    returnHandled.current = true;

    setWorking(true);
    planBillingApi
      .refresh(projectId, paymentId ? { paymentId } : {})
      .then(async () => {
        setMessage("Listo. Revisamos Mercado Pago y actualizamos tu plan.");
        await Promise.all([load(), loadProjects()]);
        const next = new URLSearchParams(searchParams);
        [
          "subscription",
          "preapproval_id",
          "billing_payment",
          "payment_id",
          "collection_id",
          "collection_status",
          "external_reference",
          "payment_type",
          "merchant_order_id",
          "preference_id",
          "site_id",
          "processing_mode",
          "merchant_account_id",
          "status",
        ].forEach((key) => next.delete(key));
        setSearchParams(next, { replace: true });
      })
      .catch((err) => setError(err.message || "No se pudo verificar el pago"))
      .finally(() => setWorking(false));
  }, [load, loadProjects, projectId, searchParams, setSearchParams]);

  const run = async (operation, successMessage) => {
    try {
      setWorking(true);
      setError("");
      setMessage("");
      const response = await operation();
      setMessage(response?.message || successMessage);
      await Promise.all([load(), loadProjects()]);
      return response;
    } catch (err) {
      setError(err.message || "No se pudo completar la operación");
      return null;
    } finally {
      setWorking(false);
    }
  };

  const billing = data?.billing || {};
  const billingAccess = getProjectBillingAccess({
    ...(data?.project || {}),
    billing,
  });
  const trialActive = billingAccess.isTrial && !billingAccess.trialExpired;
  const paymentSuspended = billingAccess.reason === "billing_suspended";
  const paymentNeedsAttention = billingAccess.needsPaymentAction;
  const services = data?.services || [];
  const serviceByCode = new Map(services.map((service) => [service.code, service]));
  const selectedServices = services.filter((service) => selectedCodes.includes(service.code));
  const selectedAmount = selectedServices.reduce(
    (total, service) => total + Number(service.monthlyPrice || 0),
    0,
  );
  const currency = selectedServices[0]?.currency || billing.currency || "ARS";
  const currentCodes = billing.pendingServiceCodes?.length
    ? billing.pendingServiceCodes
    : billing.serviceCodes || [];
  const selectionChanged = !sameCodes(selectedCodes, currentCodes);
  const recurringPendingUrl =
    billing.pendingSubscriptionUrl ||
    (billing.status === "pending" ? billing.subscriptionUrl : "");
  const periodPendingUrl = billing.pendingCheckoutUrl || "";
  const pendingCheckoutUrl =
    paymentMode === "one_time" ? periodPendingUrl : recurringPendingUrl;
  const pendingRecord = paymentMode === "one_time"
    ? (data?.checkouts || []).find(
        (checkout) =>
          checkout.status === "pending_payment" &&
          (!billing.pendingCheckoutId ||
            String(checkout.id || checkout._id) === String(billing.pendingCheckoutId)),
      )
    : (data?.subscriptions || []).find((subscription) => {
        if (subscription.status !== "pending") return false;
        const providerId = String(subscription.providerSubscriptionId || "");
        return !billing.pendingSubscriptionId || providerId === billing.pendingSubscriptionId;
      });
  const pendingPriceChanged = Boolean(
    pendingCheckoutUrl &&
      pendingRecord &&
      (Math.abs(Number(pendingRecord.amount || 0) - selectedAmount) >= 0.01 ||
        String(pendingRecord.currency || "ARS").toUpperCase() !==
          String(currency || "ARS").toUpperCase()),
  );
  const pendingPayerEmailChanged = Boolean(
    paymentMode === "recurring" &&
      pendingCheckoutUrl &&
      pendingRecord?.payerEmail &&
      normalizeEmail(pendingRecord.payerEmail) !== normalizeEmail(payerEmail),
  );
  const priceNeedsConfirmation = Boolean(
    billing.pricingOutdated || pendingPriceChanged || pendingPayerEmailChanged,
  );
  const canControlProvider =
    billing.paymentMode === "recurring" &&
    billing.provider === "mercadopago" &&
    Boolean(billing.subscriptionId);
  const canSubmit =
    Boolean(data?.canManage) &&
    Boolean(data?.mercadoPagoConfigured) &&
    selectedCodes.length > 0;
  const featureRequested = searchParams.get("feature");
  const isActive = ["active", "exempt"].includes(billing.status);
  const currentPeriodEnd = billing.currentPeriodEnd
    ? new Date(billing.currentPeriodEnd)
    : null;
  const hasFuturePaidPeriod = Boolean(
    billing.paymentMode === "one_time" &&
      billing.status === "active" &&
      currentPeriodEnd &&
      !Number.isNaN(currentPeriodEnd.getTime()) &&
      currentPeriodEnd > new Date(),
  );
  const oneTimeLocked = Boolean(
    billing.paymentMode === "recurring" &&
      billing.status === "active" &&
      billing.subscriptionId,
  );
  const recurringLocked = hasFuturePaidPeriod;
  const paymentModeChanged = Boolean(
    isActive &&
      ["recurring", "one_time"].includes(billing.paymentMode) &&
      billing.paymentMode !== paymentMode,
  );
  const recurringNeedsAuthorization =
    paymentMode === "recurring" &&
    (!isActive || !billing.isRecurring || paymentModeChanged);
  const payerEmailRequired = Boolean(
    paymentMode === "recurring" &&
      (recurringNeedsAuthorization || paymentNeedsAttention || pendingCheckoutUrl),
  );
  const payerEmailReady = !payerEmailRequired || isValidEmail(payerEmail);
  const hasCheckoutAction =
    paymentMode === "one_time" ||
    recurringNeedsAuthorization ||
    selectionChanged ||
    priceNeedsConfirmation ||
    Boolean(pendingCheckoutUrl);
  const effectivePaymentMode =
    isActive || paymentNeedsAttention
      ? billing.paymentMode === "one_time"
        ? "one_time"
        : "recurring"
      : paymentMode;
  const billingHistory = [
    ...(data?.subscriptions || []).map((item) => ({
      ...item,
      historyType: "recurring",
    })),
    ...(data?.checkouts || []).map((item) => ({
      ...item,
      historyType: "one_time",
    })),
  ].sort(
    (left, right) =>
      new Date(right.createdAt || 0).getTime() -
      new Date(left.createdAt || 0).getTime(),
  );


  const toggleService = (service, checked) => {
    if (checked) {
      const nextCodes = expandSelection([...selectedCodes, service.code], services);
      const automaticallyAdded = nextCodes.filter(
        (code) => !selectedCodes.includes(code) && code !== service.code,
      );
      setSelectedCodes(nextCodes);
      if (automaticallyAdded.length) {
        const names = automaticallyAdded
          .map((code) => serviceByCode.get(code)?.name)
          .filter(Boolean)
          .join(", ");
        setMessage(`${service.name} necesita ${names}. WebProfit los agregó automáticamente.`);
      }
      return;
    }

    const requiredBy = services.filter(
      (candidate) =>
        selectedCodes.includes(candidate.code) &&
        (candidate.requiredServiceCodes || []).includes(service.code),
    );

    if (requiredBy.length) {
      setMessage(
        `${service.name} no se puede desactivar mientras esté activo ${requiredBy
          .map((candidate) => candidate.name)
          .join(", ")}.`,
      );
      return;
    }

    setSelectedCodes((current) => removeServiceAndDependants(service.code, current, services));
  };

  const changePaymentMode = (nextMode) => {
    if (nextMode === "one_time" && oneTimeLocked) return;
    if (nextMode === "recurring" && recurringLocked) return;
    setPaymentMode(nextMode);
    setCardError("");
    setThreeDSChallenge(null);
    setMessage(
      nextMode === "one_time"
        ? "Elegiste pagar solamente este período. No habrá renovación automática."
        : "Elegiste débito automático mensual.",
    );
  };

  const saveSelection = async () => {
    if (pendingCheckoutUrl && !selectionChanged && !priceNeedsConfirmation) {
      window.location.assign(pendingCheckoutUrl);
      return;
    }
    const response = await run(
      () =>
        paymentMode === "one_time"
          ? planBillingApi.checkout(projectId, {
              serviceCodes: selectedCodes,
              paymentMode: "one_time",
              periodMonths: 1,
            })
          : planBillingApi.subscribe(projectId, {
              serviceCodes: selectedCodes,
              payerEmail: normalizeEmail(payerEmail),
            }),
      paymentMode === "one_time"
        ? "Mercado Pago está listo para cobrar este período."
        : isActive
          ? "Tu plan quedó actualizado."
          : "Tu plan está listo para autorizarse en Mercado Pago.",
    );
    if (response?.checkoutUrl) window.location.assign(response.checkoutUrl);
  };


  const payWithEmbeddedCard = async ({ formData }) => {
    try {
      setWorking(true);
      setError("");
      setCardError("");
      setMessage("");
      const response = await planBillingApi.payWithCard(projectId, {
        paymentMode,
        serviceCodes: selectedCodes,
        periodMonths: 1,
        formData,
      });

      const formEmail = normalizeEmail(formData?.payer?.email || "");
      if (formEmail) setPayerEmail(formEmail);

      if (response?.payment?.threeDS?.required) {
        setThreeDSChallenge({
          ...response.payment.threeDS,
          paymentId: response.payment.paymentId,
        });
        setMessage("El banco necesita una verificación adicional para terminar el pago.");
        return response;
      }

      setThreeDSChallenge(null);
      setMessage(
        response?.message ||
          (paymentMode === "recurring"
            ? "El débito automático quedó autorizado."
            : "El pago fue procesado."),
      );
      await Promise.all([load(), loadProjects()]);
      return response;
    } catch (err) {
      const nextMessage = err?.message || "No se pudo procesar la tarjeta";
      setCardError(nextMessage);
      setError(nextMessage);
      return null;
    } finally {
      setWorking(false);
    }
  };

  const completeThreeDSChallenge = async () => {
    const paymentId = threeDSChallenge?.paymentId;
    if (!paymentId) return;
    try {
      setWorking(true);
      setCardError("");
      setError("");
      await planBillingApi.refresh(projectId, { paymentId });
      setThreeDSChallenge(null);
      setMessage("Verificamos el pago con Mercado Pago.");
      await Promise.all([load(), loadProjects()]);
    } catch (err) {
      const nextMessage = err?.message || "No se pudo verificar el pago";
      setCardError(nextMessage);
      setError(nextMessage);
    } finally {
      setWorking(false);
    }
  };

  const updatePaymentMethod = async () => {
    const response = await run(
      () =>
        planBillingApi.updatePaymentMethod(projectId, {
          payerEmail: normalizeEmail(payerEmail),
        }),
      "Abrimos Mercado Pago para cambiar la tarjeta.",
    );
    if (response?.checkoutUrl) window.location.assign(response.checkoutUrl);
  };

  const pauseSubscription = async () => {
    if (!window.confirm("¿Pausar el plan? La landing seguirá visible, pero las funciones operativas quedarán pausadas hasta que lo reactives.")) return;
    await run(() => planBillingApi.pause(projectId), "Tu plan quedó pausado.");
  };

  const cancelSubscription = async () => {
    if (!window.confirm("¿Cancelar el plan y detener el cobro mensual de WebProfit? La landing seguirá visible, pero las funciones operativas quedarán pausadas.")) return;
    await run(() => planBillingApi.cancel(projectId), "Tu plan quedó cancelado.");
  };

  const actionLabel = pendingCheckoutUrl && !selectionChanged && !priceNeedsConfirmation
    ? "Continuar en Mercado Pago"
    : paymentMode === "one_time"
      ? "Pagar con Mercado Pago"
      : paymentNeedsAttention
        ? "Actualizar tarjeta con Mercado Pago"
        : isActive && billing.isRecurring
          ? selectionChanged || priceNeedsConfirmation
            ? "Actualizar suscripción"
            : "Suscripción activa"
          : "Suscribirme con Mercado Pago";
  const primaryAction =
    paymentNeedsAttention && paymentMode === "recurring" && canControlProvider
      ? updatePaymentMethod
      : saveSelection;
  const modeLocked =
    (paymentMode === "one_time" && oneTimeLocked) ||
    (paymentMode === "recurring" && recurringLocked);
  const primaryDisabled =
    working ||
    !canSubmit ||
    !payerEmailReady ||
    modeLocked ||
    (!paymentNeedsAttention && !hasCheckoutAction);
  const embeddedCardConfigured = Boolean(
    data?.embeddedCardConfigured && data?.mercadoPagoPublicKey,
  );
  const cardDisabled =
    working ||
    !canSubmit ||
    modeLocked ||
    selectedAmount <= 0 ||
    !embeddedCardConfigured;


  if (loading && !data) {
    return (
      <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
        <CircularProgress sx={{ color: BRAND.orange }} />
      </Box>
    );
  }

  return (
    <Stack spacing={2.5} sx={{ pb: 4, minWidth: 0 }}>
      <Paper
        className={embedded ? "wp-settings-plan-hero" : undefined}
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          p: { xs: 2.25, sm: 3, md: 3.5 },
          borderRadius: 4,
          color: "#fff",
          background: "linear-gradient(135deg,#19242F 0%,#2A4052 72%,#35566A 100%)",
          boxShadow: "0 18px 46px rgba(25,36,47,.16)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 250,
            height: 250,
            borderRadius: "50%",
            right: -90,
            top: -155,
            bgcolor: "rgba(243,90,58,.26)",
          }}
        />
        <Stack position="relative" spacing={2.5} sx={{ minWidth: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ md: "flex-start" }}
            spacing={2}
            sx={{ minWidth: 0 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ fontWeight: 900, letterSpacing: ".13em", color: "rgba(255,255,255,.62)" }}>
                TU PLAN WEBPROFIT
              </Typography>
              <Typography
                variant={embedded ? "h4" : "h3"}
                sx={{
                  mt: 0.2,
                  fontWeight: 950,
                  letterSpacing: "-.04em",
                  lineHeight: 1.08,
                  overflowWrap: "anywhere",
                }}
              >
                Elegí lo que necesita tu negocio
              </Typography>
              <Typography sx={{ mt: 1, maxWidth: 720, color: "rgba(255,255,255,.75)", lineHeight: 1.55 }}>
                Armá tu plan, elegí débito automático o un pago por período y completá todo dentro de Mercado Pago.
              </Typography>
            </Box>
            <Chip
              icon={<WorkspacePremiumRoundedIcon />}
              label={billingAccess.isGrace
                ? "Pago pendiente · período de gracia"
                : paymentSuspended
                  ? "Suspendido por pago"
                  : billingAccess.isLandingOnly
                    ? "Landing visible · funciones pausadas"
                  : trialActive
                    ? `Prueba gratis · ${billingAccess.trialDaysRemaining ?? 30} días`
                    : statusLabel(billing.status)}
              color={billingAccess.isLandingOnly || paymentSuspended ? "error" : billingAccess.isGrace ? "warning" : statusColor(billing.status)}
              sx={{
                maxWidth: "100%",
                fontWeight: 950,
                alignSelf: { xs: "flex-start", md: "center" },
                "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
              }}
            />
          </Stack>

          <Box
            className={embedded ? "wp-settings-plan-steps" : undefined}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3,minmax(0,1fr))" },
              gap: { xs: 1.1, sm: 2 },
            }}
          >
            <Step number="1">Elegí módulos</Step>
            <Step number="2">Revisá el total</Step>
            <Step number="3">Elegí cómo pagar</Step>
          </Box>
        </Stack>
      </Paper>

      <Alert severity={isActive || trialActive ? "success" : "info"} sx={{ borderRadius: 3 }}>
        {isActive
          ? "Tu suscripción activa también habilita un segundo proyecto para esta cuenta."
          : trialActive
            ? `Estás en la prueba gratis completa de 30 días. Todos los módulos funcionan hasta el ${dateLabel(billingAccess.trialEndsAt)}; después podés conservar sólo los servicios que elijas.`
            : "Al activar una suscripción, además de los módulos elegidos, habilitás un segundo proyecto para tu cuenta."}
      </Alert>

      {error ? <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 3 }}>{error}</Alert> : null}
      {message ? <Alert severity="success" onClose={() => setMessage("")} sx={{ borderRadius: 3 }}>{message}</Alert> : null}
      {featureRequested && !paymentNeedsAttention ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Llegaste para activar <strong>{featureRequested}</strong>. Buscalo abajo y tocá <strong>Agregar al plan</strong>.
        </Alert>
      ) : null}
      {!data?.mercadoPagoConfigured ? (
        <Alert severity="warning" sx={{ borderRadius: 3 }}>
          El cobro todavía no está disponible porque falta configurar el Mercado Pago de WebProfit. No se modificará tu plan hasta que esté listo.
        </Alert>
      ) : null}
      {billing.pricingOutdated ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Tu precio fue actualizado. Revisá el nuevo total y confirmalo antes del próximo cobro.
        </Alert>
      ) : null}
      {pendingPriceChanged ? (
        <Alert severity="warning" sx={{ borderRadius: 3 }}>
          El pago pendiente tiene otro importe. Al confirmar se generará una autorización con el total que ves ahora.
        </Alert>
      ) : null}
      {billingAccess.isGrace ? (
        <Alert severity="warning" icon={<WarningAmberRoundedIcon />} sx={{ borderRadius: 3, border: "1px solid rgba(237,108,2,.24)" }}>
          <Typography sx={{ fontWeight: 950 }}>Tu proyecto sigue funcionando, pero hay un pago pendiente.</Typography>
          <Typography variant="body2" sx={{ mt: 0.35 }}>
            {billing.paymentMode === "one_time"
              ? `Pagá otro período antes del ${dateLabel(billingAccess.graceUntil)}. Quedan ${billingAccess.daysRemaining ?? "pocos"} días para evitar que se pausen las funciones operativas.`
              : `Actualizá la tarjeta antes del ${dateLabel(billingAccess.graceUntil)}. Quedan ${billingAccess.daysRemaining ?? "pocos"} días para evitar que se pausen las funciones operativas.`}
          </Typography>
        </Alert>
      ) : null}
      {paymentSuspended ? (
        <Alert severity="error" icon={<LockClockRoundedIcon />} sx={{ borderRadius: 3, border: "1px solid rgba(211,47,47,.22)" }}>
          <Typography sx={{ fontWeight: 950 }}>El proyecto está suspendido por pago.</Typography>
          <Typography variant="body2" sx={{ mt: 0.35 }}>
            Tus datos siguen seguros. Regularizá la suscripción desde esta pantalla para recuperar el acceso al proyecto y sus herramientas.
          </Typography>
        </Alert>
      ) : null}
      {billingAccess.isLandingOnly ? (
        <Alert severity="error" icon={<LockClockRoundedIcon />} sx={{ borderRadius: 3, border: "1px solid rgba(211,47,47,.22)" }}>
          <Typography sx={{ fontWeight: 950 }}>
            {billingAccess.reason === "trial_expired"
              ? "La prueba gratis de 30 días terminó; la landing sigue visible."
              : "La landing sigue visible; el resto está pausado."}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.35 }}>
            {billingAccess.reason === "trial_expired"
              ? "Tus datos no se borraron. Elegí los servicios que querés conservar y WebProfit reactivará automáticamente las funciones al aprobarse el pago."
              : billing.paymentMode === "one_time"
                ? "Tus datos no se borraron. Pagá un nuevo período y WebProfit reactivará automáticamente las funciones."
                : "Tus datos no se borraron. Cambiá la tarjeta o verificá el pago y WebProfit reactivará automáticamente las funciones."}
          </Typography>
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3,minmax(0,1fr))" },
          gap: 1.5,
          minWidth: 0,
        }}
      >
        <SummaryMetric
          icon={<CreditCardRoundedIcon />}
          title={paymentNeedsAttention ? "IMPORTE A REGULARIZAR" : "IMPORTE DEL PLAN"}
          value={money(isActive || paymentNeedsAttention ? billing.monthlyAmount : selectedAmount, isActive || paymentNeedsAttention ? billing.currency : currency)}
          helper={effectivePaymentMode === "one_time" ? "Valor de un período mensual" : "Mensualidad del débito automático"}
        />
        <SummaryMetric
          icon={billingAccess.isLandingOnly || paymentSuspended ? <LockClockRoundedIcon /> : <EventAvailableRoundedIcon />}
          title={trialActive ? "PRUEBA GRATIS HASTA" : billingAccess.isGrace ? "TENÉS HASTA" : billingAccess.isLandingOnly || paymentSuspended ? "ACCESO ACTUAL" : effectivePaymentMode === "one_time" ? "SERVICIO ACTIVO HASTA" : "PRÓXIMO COBRO"}
          value={trialActive ? dateLabel(billingAccess.trialEndsAt) : billingAccess.isGrace ? dateLabel(billingAccess.graceUntil) : paymentSuspended ? "Suspendido" : billingAccess.isLandingOnly ? "Solo landing" : dateLabel(billing.nextPaymentDate || billing.currentPeriodEnd)}
          helper={trialActive ? "Acceso completo sin tarjeta" : billingAccess.isGrace ? "Después se pausarán funciones" : billingAccess.isLandingOnly || paymentSuspended ? "Se reactiva al aprobarse el pago" : effectivePaymentMode === "one_time" ? "No se renueva automáticamente" : "Renovación mensual automática"}
        />
        <SummaryMetric
          icon={<AutorenewRoundedIcon />}
          title="FORMA DE PAGO"
          value={effectivePaymentMode === "one_time" ? "Pago por período" : billing.isRecurring ? "Débito automático" : "A elegir"}
          helper={effectivePaymentMode === "one_time" ? "Un solo cobro con tarjeta o saldo disponible" : paymentNeedsAttention ? "Actualizá la tarjeta para continuar" : billing.isRecurring ? "Cobro mensual automático" : "Se configura al activar el plan"}
        />
      </Box>

      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ md: "flex-end" }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark, letterSpacing: "-.03em", lineHeight: 1.15 }}>
                {paymentNeedsAttention ? "Tu plan actual" : "Armá tu plan"}
              </Typography>
              <ContextualHelp
                compact
                title="Qué cambia al activar un módulo"
                text="Cada módulo habilita una parte concreta del panel. Si una función depende de otra, WebProfit agrega esa dependencia antes de calcular el total."
                bullets={[
                  "El precio mostrado se recalcula con la combinación seleccionada.",
                  "Desactivar una función puede requerir quitar primero las que dependen de ella.",
                  "El tipo de negocio orienta la experiencia, pero no te encierra en una sola combinación de módulos.",
                ]}
              />
            </Stack>
            <Typography sx={{ mt: 0.55, color: BRAND.muted, maxWidth: 760, lineHeight: 1.5 }}>
              {paymentNeedsAttention
                ? "Conservamos tus módulos y tus datos mientras regularizás el pago."
                : "Activá cada función con un interruptor. Cuando un módulo necesita otro, WebProfit lo incluye y te lo explica."}
            </Typography>
          </Box>
          <Chip
            icon={<WorkspacePremiumRoundedIcon />}
            label={`${selectedCodes.length} elegidos`}
            sx={{ fontWeight: 900, alignSelf: { xs: "flex-start", md: "center" } }}
          />
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0,1fr)", lg: "minmax(0,1fr) 330px" },
            gap: { xs: 2.25, lg: 2.75 },
            alignItems: "start",
            minWidth: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              minWidth: 0,
              overflow: "hidden",
              borderRadius: 3.5,
              border: `1px solid ${BRAND.border}`,
              boxShadow: "0 10px 30px rgba(25,36,47,.055)",
            }}
          >
            <Box
              sx={{
                display: { xs: "none", md: "grid" },
                gridTemplateColumns: "44px minmax(220px,1fr) minmax(120px,.55fr) 132px 92px",
                gap: 1.75,
                alignItems: "center",
                px: 2,
                py: 1.1,
                bgcolor: "rgba(25,36,47,.035)",
                borderBottom: `1px solid ${BRAND.border}`,
              }}
            >
              <Box />
              <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950 }}>
                FUNCIÓN
              </Typography>
              <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950 }}>
                INFORMACIÓN
              </Typography>
              <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950, textAlign: "right" }}>
                PRECIO
              </Typography>
              <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950, textAlign: "center" }}>
                ESTADO
              </Typography>
            </Box>

            {services.map((service) => {
              const selected = selectedCodes.includes(service.code);
              const activeNow = (billing.serviceCodes || []).includes(service.code);
              const selectable = service.active !== false || activeNow;
              const presentation = SERVICE_PRESENTATION[service.code] || {};
              const requiredNames = (service.requiredServiceCodes || [])
                .map((code) => serviceByCode.get(code)?.name)
                .filter(Boolean);
              const requiredBy = services
                .filter(
                  (candidate) =>
                    selectedCodes.includes(candidate.code) &&
                    (candidate.requiredServiceCodes || []).includes(service.code),
                )
                .map((candidate) => candidate.name);

              return (
                <ServiceRow
                  key={service.code}
                  service={service}
                  selected={selected}
                  activeNow={activeNow}
                  selectable={selectable}
                  presentation={presentation}
                  requiredNames={requiredNames}
                  requiredBy={requiredBy}
                  disabled={working || paymentNeedsAttention || !data?.canManage}
                  paymentNeedsAttention={paymentNeedsAttention}
                  onToggle={(checked) => toggleService(service, checked)}
                />
              );
            })}
          </Paper>

          <CheckoutSummary
            selectedServices={selectedServices}
            selectedAmount={selectedAmount}
            currency={currency}
            billing={billing}
            billingAccess={billingAccess}
            paymentNeedsAttention={paymentNeedsAttention}
            paymentMode={paymentMode}
            onPaymentModeChange={changePaymentMode}
            payerEmail={payerEmail}
            onPayerEmailChange={setPayerEmail}
            payerEmailRequired={payerEmailRequired}
            recurringLocked={recurringLocked}
            oneTimeLocked={oneTimeLocked}
            selectionChanged={selectionChanged}
            actionLabel={actionLabel}
            primaryDisabled={primaryDisabled}
            primaryAction={primaryAction}
            working={working}
            mercadoPagoPublicKey={data?.mercadoPagoPublicKey || ""}
            embeddedCardConfigured={embeddedCardConfigured}
            cardDisabled={cardDisabled}
            onCardSubmit={payWithEmbeddedCard}
            cardError={cardError}
            threeDSChallenge={threeDSChallenge}
            onThreeDSComplete={completeThreeDSChallenge}
            recurringActive={Boolean(isActive && billing.isRecurring)}
          />
        </Box>
      </Box>

      {(canControlProvider || paymentNeedsAttention) ? (
        <Paper elevation={0} sx={{ p: { xs: 2.25, md: 2.75 }, borderRadius: 3.5, border: `1px solid ${BRAND.border}` }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                {paymentNeedsAttention
                  ? paymentMode === "one_time"
                    ? "Renovar el período"
                    : "Regularizar el débito automático"
                  : "Administrar el débito automático"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: BRAND.muted }}>
                {paymentNeedsAttention
                  ? paymentMode === "one_time"
                    ? "Pagá un período sin guardar una renovación automática. Al aprobarse, las funciones se reactivan."
                    : "Actualizá la tarjeta o verificá el cobro pendiente para mantener el servicio activo."
                  : "Verificá el estado, cambiá la tarjeta o administrá la renovación mensual."}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {paymentNeedsAttention ? (
                <>
                  <Button
                    disabled={working || !data?.canManage || !data?.mercadoPagoConfigured || selectedCodes.length === 0 || modeLocked || (paymentMode === "recurring" && !isValidEmail(payerEmail))}
                    startIcon={<CreditCardRoundedIcon />}
                    onClick={primaryAction}
                    variant="contained"
                    sx={{ bgcolor: BRAND.orange, borderRadius: 2.5, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: BRAND.orangeDark } }}
                  >
                    {paymentMode === "one_time" ? "Pagar un período" : "Cambiar tarjeta"}
                  </Button>
                  <Button
                    disabled={working}
                    startIcon={<SyncRoundedIcon />}
                    onClick={() => run(() => planBillingApi.refresh(projectId), "Estado actualizado.")}
                    sx={{ textTransform: "none", fontWeight: 900 }}
                  >
                    Ya pagué, verificar
                  </Button>
                </>
              ) : canControlProvider ? (
                <>
                  <Button disabled={working} startIcon={<SyncRoundedIcon />} onClick={() => run(() => planBillingApi.refresh(projectId), "Estado actualizado.")} sx={{ textTransform: "none", fontWeight: 900 }}>
                    Verificar pago
                  </Button>
                  <Button disabled={working || !data?.canManage || !isValidEmail(payerEmail)} startIcon={<CreditCardRoundedIcon />} onClick={updatePaymentMethod} sx={{ textTransform: "none", fontWeight: 900 }}>
                    Cambiar tarjeta
                  </Button>
                  {billing.status === "paused" ? (
                    <Button disabled={working || !data?.canManage} startIcon={<PlayCircleRoundedIcon />} onClick={() => run(() => planBillingApi.resume(projectId), "Tu plan quedó activo nuevamente.")} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 2.5, textTransform: "none", fontWeight: 900, "&:hover": { bgcolor: BRAND.orangeDark } }}>
                      Reactivar plan
                    </Button>
                  ) : (
                    <Button disabled={working || !data?.canManage || billing.status === "cancelled"} startIcon={<PauseCircleRoundedIcon />} onClick={pauseSubscription} sx={{ textTransform: "none", fontWeight: 900 }}>
                      Pausar
                    </Button>
                  )}
                  <Button disabled={working || !data?.canManage || billing.status === "cancelled"} color="error" startIcon={<CancelRoundedIcon />} onClick={cancelSubscription} sx={{ textTransform: "none", fontWeight: 900 }}>
                    Cancelar
                  </Button>
                </>
              ) : null}
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      <Accordion elevation={0} disableGutters sx={{ borderRadius: "14px !important", border: `1px solid ${BRAND.border}`, overflow: "hidden", "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: { xs: 2.25, md: 3 }, py: 0.75 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
              Historial de pagos y cambios
            </Typography>
            <Typography variant="body2" sx={{ color: BRAND.muted }}>
              Revisá autorizaciones, importes y estados anteriores.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1.25, md: 3 }, pb: 3 }}>
          <TableContainer sx={{ maxWidth: "100%" }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Plan elegido</TableCell>
                  <TableCell>Modalidad</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Importe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingHistory.map((entry) => (
                  <TableRow key={`${entry.historyType}-${entry.id || entry._id}`} hover>
                    <TableCell>{dateLabel(entry.createdAt)}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{subscriptionServicesLabel(entry)}</TableCell>
                    <TableCell>
                      {entry.historyType === "one_time"
                        ? "Pago por período"
                        : entry.provider === "mercadopago"
                          ? "Débito automático"
                          : "Manual"}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={statusLabel(entry.status)} color={statusColor(entry.status)} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>
                      {money(entry.amount, entry.currency)}
                    </TableCell>
                  </TableRow>
                ))}
                {!billingHistory.length ? (
                  <TableRow>
                    <TableCell colSpan={5}>Todavía no hay pagos o cambios registrados.</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
